/**
 * BarometerOccupationFilter component.
 * Provides autocomplete search for occupations from the labour barometer,
 * and a multi-select status dropdown for filtering by deficit level.
 *
 * Used in CallsAdvancedFilters modal for the "Barometr zawodów" section.
 *
 * @see docs/specifications/2026-03-10-barometer-occupation-filter.md
 */

import React, {
  useState,
  useRef,
  useCallback,
  useEffect,
  useId,
} from "react";
import apiClient from "../../../services/apiClient";
import { useDebounce } from "../../../hooks/useDebounce";
import { useClickOutside } from "../../../hooks/useClickOutside";
import { BAROMETER_STATUSES } from "../../../constants/barometer";
export type { BarometerStatus } from "../../../constants/barometer";

/** Maximum number of occupations that can be selected at once */
const MAX_OCCUPATIONS = 3;

/** Minimum characters to trigger autocomplete search */
const MIN_SEARCH_LENGTH = 2;

interface BarometerOccupationFilterProps {
  /** Currently selected occupation names */
  selectedOccupations: string[];
  /** Callback when occupations selection changes */
  onOccupationsChange: (occupations: string[]) => void;
  /** Currently selected barometer statuses */
  selectedStatuses: readonly string[];
  /** Callback when statuses selection changes */
  onStatusesChange: (statuses: string[]) => void;
}

/**
 * Fetches occupation suggestions from the barometer API.
 * Calls GET /grants/barometer/occupations?search=...&statuses[]=...
 */
async function fetchOccupationSuggestions(
  search: string,
  statuses: readonly string[]
): Promise<string[]> {
  const params = new URLSearchParams();
  params.append("search", search);
  statuses.forEach((s) => params.append("statuses[]", s));
  const { data } = await apiClient.get(
    `/grants/barometer/occupations?${params.toString()}`
  );
  // Fix #9: Validate API response instead of blind cast
  return Array.isArray(data.occupations)
    ? data.occupations.filter(
        (o: unknown): o is string => typeof o === "string"
      )
    : [];
}

/**
 * BarometerOccupationFilter provides:
 * - Autocomplete input with debounce for searching occupations
 * - Selected occupation chips (max 3) with remove buttons
 * - Multi-select status dropdown for deficit levels
 * - Active filter badge when occupations are selected
 * - Full keyboard navigation (Arrow keys, Enter, Escape)
 * - ARIA attributes for accessibility (role=combobox, aria-expanded, aria-activedescendant)
 */
const BarometerOccupationFilter: React.FC<BarometerOccupationFilterProps> = ({
  selectedOccupations,
  onOccupationsChange,
  selectedStatuses,
  onStatusesChange,
}) => {
  const [searchInput, setSearchInput] = useState("");
  const [suggestions, setSuggestions] = useState<string[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const [activeIndex, setActiveIndex] = useState(-1);
  const [isStatusOpen, setIsStatusOpen] = useState(false);
  // Fix #10: Error state for API failure feedback
  const [hasError, setHasError] = useState(false);

  const inputRef = useRef<HTMLInputElement>(null);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const statusDropdownRef = useRef<HTMLDivElement>(null);
  const listboxId = useId();
  // Fix #2: Dedicated ID for the input element
  const inputId = useId();

  const debouncedSearch = useDebounce(searchInput, 400);

  // Fix #3: Use ref for selectedStatuses to avoid re-triggering autocomplete on status change
  const statusesRef = useRef(selectedStatuses);
  statusesRef.current = selectedStatuses;

  // Close autocomplete dropdown on click outside
  const handleClickOutsideDropdown = useCallback(() => {
    setIsDropdownOpen(false);
    setActiveIndex(-1);
  }, []);
  useClickOutside(dropdownRef, handleClickOutsideDropdown, isDropdownOpen);

  // Close status dropdown on click outside
  const handleClickOutsideStatus = useCallback(() => {
    setIsStatusOpen(false);
  }, []);
  useClickOutside(statusDropdownRef, handleClickOutsideStatus, isStatusOpen);

  // Fetch suggestions when debounced search changes
  useEffect(() => {
    if (debouncedSearch.length < MIN_SEARCH_LENGTH) {
      setSuggestions([]);
      setIsDropdownOpen(false);
      setIsLoading(false);
      return;
    }

    let cancelled = false;
    setIsLoading(true);
    // Fix #10: Clear error when starting a new search
    setHasError(false);

    // Fix #3: Use statusesRef.current instead of selectedStatuses dependency
    fetchOccupationSuggestions(debouncedSearch, statusesRef.current)
      .then((result) => {
        if (!cancelled) {
          setSuggestions(result);
          setIsDropdownOpen(true);
          setIsLoading(false);
          setActiveIndex(-1);
        }
      })
      .catch(() => {
        if (!cancelled) {
          setSuggestions([]);
          setIsDropdownOpen(true);
          setIsLoading(false);
          // Fix #10: Set error state on API failure
          setHasError(true);
        }
      });

    return () => {
      cancelled = true;
    };
  }, [debouncedSearch]);

  // Note: dropdown is opened by the fetch effect above; no re-open logic needed here.

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    setSearchInput(value);
    if (value.length < MIN_SEARCH_LENGTH) {
      setIsDropdownOpen(false);
      setSuggestions([]);
      setActiveIndex(-1);
    }
  };

  const handleSelectOccupation = useCallback(
    (occupation: string) => {
      if (selectedOccupations.length >= MAX_OCCUPATIONS) return;
      if (selectedOccupations.includes(occupation)) return;
      onOccupationsChange([...selectedOccupations, occupation]);
      setSearchInput("");
      setSuggestions([]);
      setIsDropdownOpen(false);
      setActiveIndex(-1);
    },
    [selectedOccupations, onOccupationsChange]
  );

  const handleRemoveOccupation = useCallback(
    (occupation: string) => {
      onOccupationsChange(selectedOccupations.filter((o) => o !== occupation));
    },
    [selectedOccupations, onOccupationsChange]
  );

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    // Fix #1: Only stopPropagation when dropdown is open (prevents modal close)
    if (e.key === "Escape") {
      if (isDropdownOpen) {
        e.stopPropagation();
        setIsDropdownOpen(false);
        setSuggestions([]);
        setActiveIndex(-1);
      }
      return;
    }

    if (!isDropdownOpen) return;

    // Fix #8: Removed dead code duplicate "Escape" case from switch
    switch (e.key) {
      case "ArrowDown":
        e.preventDefault();
        setActiveIndex((prev) =>
          prev < suggestions.length - 1 ? prev + 1 : prev
        );
        break;
      case "ArrowUp":
        e.preventDefault();
        setActiveIndex((prev) => (prev > 0 ? prev - 1 : -1));
        break;
      case "Enter":
        e.preventDefault();
        if (activeIndex >= 0 && activeIndex < suggestions.length) {
          handleSelectOccupation(suggestions[activeIndex]);
        }
        break;
    }
  };

  const handleStatusToggle = (status: string) => {
    if (selectedStatuses.includes(status)) {
      onStatusesChange(selectedStatuses.filter((s) => s !== status));
    } else {
      onStatusesChange([...selectedStatuses, status]);
    }
  };

  // Fix #6: Escape key handler for status dropdown
  const handleStatusKeyDown = (e: React.KeyboardEvent<HTMLButtonElement>) => {
    if (e.key === "Escape" && isStatusOpen) {
      e.stopPropagation();
      setIsStatusOpen(false);
    }
  };

  const isMaxReached = selectedOccupations.length >= MAX_OCCUPATIONS;

  // Build active descendant ID for keyboard navigation
  const activeDescendantId =
    isDropdownOpen && activeIndex >= 0
      ? `${listboxId}-option-${activeIndex}`
      : undefined;

  return (
    <div className="space-y-3">
      {/* Section header */}
      <div className="flex items-center gap-2">
        {/* Fix #2: Link label to input via htmlFor */}
        <label
          htmlFor={inputId}
          className="block text-sm font-medium text-neutral-700"
        >
          Barometr zawodów
        </label>
        {selectedOccupations.length > 0 && (
          <span
            data-testid="barometer-filter-active-badge"
            className="inline-flex items-center justify-center px-2 py-0.5 text-xs font-medium text-primary-700 bg-primary-100 rounded-full"
          >
            {selectedOccupations.length}
          </span>
        )}
      </div>

      {/* Selected occupation chips */}
      {selectedOccupations.length > 0 && (
        <div className="flex flex-wrap gap-1.5">
          {selectedOccupations.map((occupation) => (
            <span
              key={occupation}
              className="inline-flex items-center gap-1 px-2.5 py-1 text-sm font-medium text-primary-800 bg-primary-50 border border-primary-200 rounded-full"
            >
              {occupation}
              <button
                type="button"
                aria-label={`usuń zawód ${occupation}`}
                onClick={() => handleRemoveOccupation(occupation)}
                className="ml-0.5 text-primary-700 hover:text-primary-800 focus:outline-none focus:ring-1 focus:ring-primary-600 rounded-full"
              >
                <svg
                  className="w-3.5 h-3.5"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                  aria-hidden="true"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M6 18L18 6M6 6l12 12"
                  />
                </svg>
              </button>
            </span>
          ))}
        </div>
      )}

      {/* Autocomplete input */}
      <div className="relative" ref={dropdownRef}>
        <div className="relative">
          {/* Fix #2: Added id={inputId} to link with label */}
          {/* Fix #7: Contextual placeholder when max reached */}
          <input
            ref={inputRef}
            id={inputId}
            type="text"
            role="combobox"
            aria-expanded={isDropdownOpen}
            aria-autocomplete="list"
            aria-controls={listboxId}
            aria-activedescendant={activeDescendantId}
            value={searchInput}
            onChange={handleInputChange}
            onKeyDown={handleKeyDown}
            placeholder={
              isMaxReached
                ? "Osiągnięto limit 3 zawodów"
                : "Wpisz zawód (min. 2 znaki)..."
            }
            disabled={isMaxReached}
            className={`w-full px-3 py-2 pr-8 border border-neutral-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500 ${
              isMaxReached ? "opacity-50 cursor-not-allowed bg-neutral-50" : "bg-white"
            }`}
          />
          {/* Spinner */}
          {isLoading && (
            <div
              data-testid="occupation-autocomplete-spinner"
              className="absolute inset-y-0 right-2 flex items-center pointer-events-none"
              aria-hidden="true"
            >
              <span className="animate-spin h-4 w-4 border-2 border-primary-600 border-t-transparent rounded-full" />
            </div>
          )}
        </div>

        {/* Fix #11: aria-live region for dynamic result announcements */}
        <span className="sr-only" aria-live="polite">
          {isDropdownOpen && suggestions.length > 0
            ? `${suggestions.length} wyników`
            : ""}
        </span>

        {/* Dropdown listbox */}
        {isDropdownOpen && (
          <ul
            id={listboxId}
            role="listbox"
            aria-label="Propozycje zawodów"
            className="absolute z-50 mt-1 w-full bg-white border border-neutral-300 rounded-md shadow-lg max-h-60 overflow-y-auto"
          >
            {suggestions.length === 0 ? (
              // Fix #5: Added role="option" with aria-disabled to empty result item
              // Fix #10: Show error message when API failed
              <li
                role="option"
                aria-disabled="true"
                aria-selected="false"
                className="px-3 py-2 text-sm text-neutral-500"
              >
                {hasError
                  ? "Błąd ładowania wyników. Spróbuj ponownie."
                  : "Brak wyników"}
              </li>
            ) : (
              suggestions.map((occupation, index) => {
                const isDisabled = isMaxReached;
                // Fix #12: Detect already-selected occupations
                const isAlreadySelected =
                  selectedOccupations.includes(occupation);
                return (
                  <li
                    key={occupation}
                    id={`${listboxId}-option-${index}`}
                    role="option"
                    aria-selected={isAlreadySelected}
                    aria-disabled={isDisabled}
                    onClick={() =>
                      !isDisabled && handleSelectOccupation(occupation)
                    }
                    className={`px-3 py-2 text-sm cursor-pointer ${
                      isDisabled
                        ? "text-neutral-400 cursor-not-allowed"
                        : activeIndex === index
                        ? "bg-primary-100 text-primary-900"
                        : "text-neutral-900 hover:bg-neutral-50"
                    }`}
                  >
                    {/* Fix #12: Show checkmark for already-selected items */}
                    {isAlreadySelected && (
                      <svg
                        className="inline-block w-4 h-4 mr-1 text-primary-600"
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                        aria-hidden="true"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M5 13l4 4L19 7"
                        />
                      </svg>
                    )}
                    {isAlreadySelected ? (
                      <strong>{occupation}</strong>
                    ) : (
                      occupation
                    )}
                  </li>
                );
              })
            )}
          </ul>
        )}
      </div>

      {isMaxReached && (
        <p className="text-xs text-neutral-500">
          Wybrano maksymalną liczbę zawodów ({MAX_OCCUPATIONS}).
        </p>
      )}

      {/* Status dropdown */}
      <div className="relative" ref={statusDropdownRef}>
        {/* Fix #6: Added onKeyDown handler for Escape key */}
        <button
          type="button"
          aria-label="Status barometru"
          aria-expanded={isStatusOpen}
          aria-haspopup="listbox"
          onClick={() => setIsStatusOpen(!isStatusOpen)}
          onKeyDown={handleStatusKeyDown}
          className="relative w-full bg-white border border-neutral-300 rounded-md pl-3 pr-10 py-2 text-left text-sm focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500 cursor-pointer hover:border-neutral-400"
        >
          <span
            className={
              selectedStatuses.length === 0
                ? "text-neutral-500"
                : "text-neutral-900"
            }
          >
            {selectedStatuses.length === 0
              ? "Wybierz statusy barometru..."
              : selectedStatuses.length <= 2
              ? selectedStatuses.join(", ")
              : `${selectedStatuses.slice(0, 2).join(", ")} i ${selectedStatuses.length - 2} więcej`}
          </span>
          <span className="absolute inset-y-0 right-0 flex items-center pr-2 pointer-events-none">
            <svg
              className={`h-5 w-5 text-neutral-400 transition-transform ${isStatusOpen ? "rotate-180" : ""}`}
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
              aria-hidden="true"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M19 9l-7 7-7-7"
              />
            </svg>
          </span>
        </button>

        {isStatusOpen && (
          <div className="absolute z-50 mt-1 w-full bg-white border border-neutral-300 rounded-md shadow-lg">
            {BAROMETER_STATUSES.map((status) => (
              <label
                key={status}
                htmlFor={`barometer-status-${status}`}
                className="flex items-center px-3 py-2 hover:bg-neutral-50 cursor-pointer"
              >
                {/* Fix #4: Removed duplicate aria-label (htmlFor/id linkage is sufficient) */}
                <input
                  type="checkbox"
                  id={`barometer-status-${status}`}
                  checked={selectedStatuses.includes(status)}
                  onChange={() => handleStatusToggle(status)}
                  className="h-4 w-4 text-primary-600 border-neutral-300 rounded focus:ring-primary-500"
                />
                <span className="ml-3 text-sm text-neutral-900">{status}</span>
              </label>
            ))}
          </div>
        )}
      </div>

      <p className="text-xs text-neutral-500">
        Filtruj nabory w powiatach z wybranym poziomem deficytu zawodu (maks.{" "}
        {MAX_OCCUPATIONS} zawody)
      </p>
    </div>
  );
};

export default BarometerOccupationFilter;
